import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { setErrorAlert, setLoader } from '../../../../../store/alert/alertSlice';
import { updateCustomForFocusThunk, getDataForFocusThunk } from '../../../../../store/focuspage/focuspageThunk';
import { uploadImage } from '../../../../../utils/upload';
import LayoutHeader from '../../../../../layout/LayoutHeader';
import DizeeInput from '../../../../../components/DizeeComponent/DizeeInput';
import AddedCustom from '../../../../../components/Focuspage/Type/Custom/AddedCustom';
import OverlayCustom from '../../../../../components/Focuspage/Type/Custom/OverlayCustom';
import Check from '../../../../../svg/Check';
import { clearFocusSection } from '../../../../../store/focuspage/focuspageSlice';

export default function EditSocialContent() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const token = localStorage.getItem('dizeeToken');

    // Get data from navigation state
    const pageData = location.state?.pageData;
    const pageId = location.state?.pageId;

    // Redirect if no pageData
    useEffect(() => {
        if (!pageData || !pageId) {
            navigate('/focus-page/custom-page');
        }
    }, [pageData, pageId, navigate]);

    // Initialize state with existing social content
    const [socialItems, setSocialItems] = useState(
        location.state?.socialContent?.map(item => ({
            ...item,
            preview: item.avatar,
            image: null
        })) || []
    );

    const [sectionTitle, setSectionTitle] = useState(location.state?.sectionTitle || '');
    const [currentSocial, setCurrentSocial] = useState({
        link: '',
        image: null,
        preview: null,
        type: 'social'
    });

    const [isOverlayVisible, setIsOverlayVisible] = useState(false);
    const [selectedSocial, setSelectedSocial] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(null);

    // URL validation
    const isValidUrl = (url) => {
        if (!url) return false;
        const tldPattern = /\.[a-z]{1,}$/i;
        try {
            const urlObj = new URL(url);
            return tldPattern.test(urlObj.hostname);
        } catch {
            return false;
        }
    };

    // Handle social click for editing
    const handleSocialClick = (item, index) => {
        setSelectedSocial({
            ...item,
            type: 'social'
        });
        setSelectedIndex(index);
        
        // Populate the currentSocial with the selected item's data
        setCurrentSocial({
            link: item.link,
            preview: item.avatar,
            image: null,
            type: 'social'
        });
        
        setIsOverlayVisible(true);
    };

    // Handle edit
    const handleEditSocial = () => {
        if (selectedSocial && selectedIndex !== null) {
            // Don't remove the item, just update it in place
            const updatedItems = [...socialItems];
            updatedItems[selectedIndex] = {
                ...updatedItems[selectedIndex],
                sectionTitle: sectionTitle,
                link: currentSocial.link,
                avatar: currentSocial.preview,
                preview: currentSocial.preview,
                image: currentSocial.image,
                type: 'social'
            };
            setSocialItems(updatedItems);
            setIsOverlayVisible(false);
        }
    };

    // Handle adding another item
    const handleAddAnother = () => {
        if (!validateForm()) return;

        if (selectedIndex !== null) {
            // If editing, update in place
            const updatedItems = [...socialItems];
            updatedItems[selectedIndex] = {
                type: 'social',
                sectionTitle: sectionTitle,
                link: currentSocial.link,
                avatar: currentSocial.preview,
                preview: currentSocial.preview,
                image: currentSocial.image
            };
            setSocialItems(updatedItems);
            setSelectedIndex(null);
        } else {
            // Add new item
            setSocialItems([...socialItems, {
                type: 'social',
                sectionTitle: sectionTitle,
                link: currentSocial.link,
                avatar: currentSocial.preview,
                preview: currentSocial.preview,
                image: currentSocial.image
            }]);
        }

        // Reset form
        setCurrentSocial({
            link: '',
            image: null,
            preview: null,
            type: 'social'
        });
    };

    // Form validation
    const validateForm = () => {
        if (!sectionTitle.trim()) {
            dispatch(setErrorAlert('Please enter a section title'));
            return false;
        }

        if (!currentSocial.preview) {
            dispatch(setErrorAlert('Please upload an image'));
            return false;
        }

        if (!currentSocial.link || !isValidUrl(currentSocial.link)) {
            dispatch(setErrorAlert('Please enter a valid URL'));
            return false;
        }

        return true;
    };

    // Form validation flags
    const isFormComplete = currentSocial.preview && 
                          currentSocial.link && 
                          isValidUrl(currentSocial.link) && 
                          sectionTitle.trim() !== '';

    const isDoneValid = () => {
        return socialItems.length > 0 || isFormComplete;
    };

    // Handle link input
    const handleLinkChange = (e) => {
        let value = e.target.value;
        if (value && !value.startsWith('http')) {
            value = `https://${value}`;
        }
        setCurrentSocial({ ...currentSocial, link: value });
    };

    // Handle image upload
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                dispatch(setErrorAlert('Image size should be less than 5MB'));
                return;
            }

            const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                dispatch(setErrorAlert('Please upload a valid image file (JPEG, PNG, GIF, or WEBP)'));
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setCurrentSocial({
                    ...currentSocial,
                    image: file,
                    preview: reader.result
                });
            };
            reader.readAsDataURL(file);
        }
        e.target.value = '';
    };

    // Handle delete
    const handleDeleteSocial = () => {
        setSocialItems(socialItems.filter((_, index) => index !== selectedIndex));
        setIsOverlayVisible(false);
    };

    // Handle move up/down
    const handleMoveUp = () => {
        if (selectedIndex > 0) {
            const updatedItems = [...socialItems];
            [updatedItems[selectedIndex], updatedItems[selectedIndex - 1]] = 
            [updatedItems[selectedIndex - 1], updatedItems[selectedIndex]];
            setSocialItems(updatedItems);
            setSelectedIndex(selectedIndex - 1);
        }
    };

    const handleMoveDown = () => {
        if (selectedIndex < socialItems.length - 1) {
            const updatedItems = [...socialItems];
            [updatedItems[selectedIndex], updatedItems[selectedIndex + 1]] = 
            [updatedItems[selectedIndex + 1], updatedItems[selectedIndex]];
            setSocialItems(updatedItems);
            setSelectedIndex(selectedIndex + 1);
        }
    };

    // Handle final submission
    const handleDone = async () => {
        if (!pageData?.page) {
            dispatch(setErrorAlert('Invalid page data'));
            return;
        }

        // Create final social items array
        let finalSocialItems = [...socialItems];
        
        // If we have content in the form, add it before submitting
        if (isFormComplete) {
            if (selectedIndex !== null) {
                // If editing, update in place
                finalSocialItems[selectedIndex] = {
                    type: 'social',
                    sectionTitle: sectionTitle,
                    link: currentSocial.link,
                    avatar: currentSocial.preview,
                    preview: currentSocial.preview,
                    image: currentSocial.image
                };
            } else {
                // If not editing, add as new item
                finalSocialItems.push({
                    type: 'social',
                    sectionTitle: sectionTitle,
                    link: currentSocial.link,
                    avatar: currentSocial.preview,
                    preview: currentSocial.preview,
                    image: currentSocial.image
                });
            }
        }

        if (finalSocialItems.length === 0) {
            dispatch(setErrorAlert('Please add at least one social link'));
            return;
        }

        dispatch(setLoader(true));

        try {
            // Process social items
            const processedSocials = await Promise.all(finalSocialItems.map(async (entry) => {
                let avatarUrl = entry.avatar;
                
                if (entry.image) {
                    avatarUrl = await uploadImage(entry.image);
                    if (!avatarUrl) {
                        throw new Error('Failed to upload image');
                    }
                }

                return {
                    type: 'social',
                    sectionTitle: sectionTitle,
                    link: entry.link || '',
                    avatar: avatarUrl
                };
            }));

            // Structure update data like in EditGalleryContent
            const updateData = {
                token,
                payload: {
                    ...pageData,
                    page: pageData.page,
                    type: 'custom',
                    headeractive: pageData.headeractive ?? true,
                    background: pageData.background ?? '1A1A25',
                    accent: pageData.accent ?? 'FFFFFF',
                    content: [
                        ...(pageData.content || []).filter(item => item.type !== 'social'),
                        ...processedSocials
                    ],
                    headerImage: pageData.headerImage,
                    headerImageUrl: pageData.headerImageUrl,
                    originalPage: pageData.page
                }
            };

            console.log('Sending update data:', updateData);
            const result = await dispatch(updateCustomForFocusThunk(updateData)).unwrap();
            
            if (result.status === 200) {
                await dispatch(getDataForFocusThunk({ token }));
                dispatch(clearFocusSection());
                navigate('/focus-page/custom-page');
            } else {
                throw new Error(result.message || 'Update failed');
            }

        } catch (error) {
            console.error('Error updating social content:', error);
            dispatch(setErrorAlert(error?.message || 'Failed to update social links'));
        } finally {
            dispatch(setLoader(false));
        }
    };

    return (
        <LayoutHeader>
            <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                {/* Fixed Header */}
                <div className='px-4 my-[50px] flex w-full justify-between'>
                    <div className='flex items-center gap-x-[16px]'>
                        <span className='text-white'>Edit content</span>
                    </div>
                    <div className='flex gap-[20px]'>
                        {isFormComplete && (
                            <button 
                                onClick={handleAddAnother}
                                className="text-white cursor-pointer"
                            >
                                Another
                            </button>
                        )}
                        <button 
                            onClick={handleDone} 
                            className={`text-white cursor-pointer ${!isDoneValid() ? 'opacity-50 pointer-events-none' : ''}`}
                        >
                            Done
                        </button>
                        <p 
                            onClick={() => navigate(-1)} 
                            className='text-white cursor-pointer'
                        >
                            Go back
                        </p>
                    </div>
                </div>

                {/* Form Content */}
                <div className="w-full px-4 overflow-y-auto pb-[100px]">
                    {/* Input Section */}
                    <div className="mb-8">
                        {/* Section Title */}
                        <div className="flex items-center mb-4">
                            <DizeeInput
                                placeholder="Enter section title (applies to all content)"
                                onChange={(e) => setSectionTitle(e.target.value)}
                                value={sectionTitle}
                            />
                            {sectionTitle && <Check className="h-[12px] w-[18px] ml-2" />}
                        </div>

                        {/* Link Input */}
                        <div className="flex items-center mb-4">
                            <DizeeInput
                                placeholder="Enter link"
                                onChange={handleLinkChange}
                                value={currentSocial.link}
                            />
                            {isValidUrl(currentSocial.link) && <Check className="h-[12px] w-[18px] ml-2" />}
                        </div>

                        {/* Image Upload */}
                        <div className="w-full mt-5">
                            <p 
                                className="text-white text-[12px] mb-2 cursor-pointer"
                                onClick={() => document.getElementById('file-input').click()}
                            >
                                {currentSocial.preview ? 'Change image' : 'Upload image'}
                            </p>
                            {currentSocial.preview && (
                                <div className="w-[50px] h-[50px] rounded-[3px] overflow-hidden">
                                    <img src={currentSocial.preview} alt="preview" className="w-full h-full object-cover" />
                                </div>
                            )}
                            <input
                                id="file-input"
                                type="file"
                                onChange={handleImageChange}
                                className="hidden"
                                accept="image/*"
                            />
                        </div>
                    </div>

                    {/* Added Social Items */}
                    {socialItems.length > 0 && (
                        <div className="flex flex-col w-full mt-[40px]">
                            <div className='pb-[20px] flex w-full items-center'>
                                <div className='flex flex-row text-white w-full'>
                                    <p>Added Social Links</p>
                                </div>
                            </div>
                            <div className="space-y-4">
                                {socialItems.map((item, index) => (
                                    <AddedCustom
                                        key={index}
                                        item={{
                                            ...item,
                                            primaryText: item.link,
                                        }}
                                        setOverlayVisible={setIsOverlayVisible}
                                        setItemForBackend={(item) => handleSocialClick(item, index)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Fixed Footer */}
                <div className="w-full absolute bottom-0 left-0">
                    <div className="w-full h-[20px] bg-gradient-to-t from-black to-transparent" />
                    <div className="w-full bg-black px-4 py-6">
                        <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5] max-w-full">
                            <p>Edit your social content.</p>
                            <p>Image and link are required.</p>
                        </div>
                    </div>
                </div>

                {/* Overlay */}
                {isOverlayVisible && (
                    <OverlayCustom
                        isOverlayVisible={isOverlayVisible}
                        setOverlayVisible={setIsOverlayVisible}
                        selectedItem={selectedSocial}
                        handleEdit={handleEditSocial}
                        handleDelete={handleDeleteSocial}
                        handleMoveUp={handleMoveUp}
                        handleMoveDown={handleMoveDown}
                        canMoveUp={selectedIndex > 0}
                        canMoveDown={selectedIndex < socialItems.length - 1}
                    />
                )}
            </div>
        </LayoutHeader>
    );
} 